import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Eye, EyeOff, CheckCircle2, XCircle, Shield, Mail, Phone, User, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DashboardLayout from '../components/DashboardLayout';
import { API, useAuth } from '../context/AuthContext';
import { trackEvent } from '@/lib/posthog';

const ROLE_LABELS = { admin: 'Administrador', resident: 'Residente', staff: 'Vigilante', super_admin: 'Super Admin' };
const ROLE_COLORS = {
  super_admin: 'bg-orange-100 text-orange-700',
  admin:    'bg-purple-100 text-purple-700',
  resident: 'bg-blue-100 text-blue-700',
  staff:    'bg-amber-100 text-amber-700'
};

const Req = ({ met, text }) => (
  <li className={`flex items-center gap-1.5 text-xs transition-colors ${met ? 'text-green-600' : 'text-slate-400'}`}>
    {met ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> : <XCircle className="h-3.5 w-3.5 shrink-0" />}
    {text}
  </li>
);

const ProfilePage = () => {
  const { user: authUser, updateUser } = useAuth();
  const fileInputRef = useRef(null);

  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);

  // Form fields
  const [name, setName]   = useState('');
  const [phone, setPhone] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photoPreview, setPhotoPreview] = useState('');

  // Password change
  const [showPwd, setShowPwd]         = useState({ current: false, new: false, confirm: false });
  const [currentPwd, setCurrentPwd]   = useState('');
  const [newPwd, setNewPwd]           = useState('');
  const [confirmPwd, setConfirmPwd]   = useState('');
  const [pwdSaving, setPwdSaving]     = useState(false);

  const hasMin   = newPwd.length >= 8;
  const hasUpper = /[A-Z]/.test(newPwd);
  const hasNum   = /[0-9]/.test(newPwd);
  const pwdMatch = newPwd === confirmPwd && confirmPwd.length > 0;

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/profile`);
        setProfile(data);
        setName(data.name || '');
        setPhone(data.phone || '');
        setPhotoUrl(data.photo_url || '');
        setPhotoPreview(data.photo_url || '');
      } catch { toast.error('No se pudo cargar el perfil'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500_000) {
      toast.error('La imagen no puede superar 500KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhotoPreview(ev.target.result);
      setPhotoUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (name !== profile?.name) payload.name = name;
      if (phone !== (profile?.phone || '')) payload.phone = phone;
      if (photoUrl !== (profile?.photo_url || '')) payload.photo_url = photoUrl;

      if (Object.keys(payload).length === 0) {
        toast.info('No hay cambios para guardar');
        return;
      }
      const { data } = await axios.patch(`${API}/profile`, payload);
      setProfile(prev => ({ ...prev, ...data }));
      updateUser({ name: data.name, photo_url: data.photo_url, phone: data.phone });
      toast.success('Perfil actualizado correctamente');
      // Track profile update
      trackEvent('perfil_updated', { role: authUser?.role });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar cambios');
    } finally { setSaving(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!hasMin || !hasUpper || !hasNum) { toast.error('La contraseña no cumple los requisitos'); return; }
    if (newPwd !== confirmPwd) { toast.error('Las contraseñas no coinciden'); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API}/profile/change-password`, {
        current_password: currentPwd,
        new_password: newPwd
      });
      toast.success('Contraseña actualizada correctamente');
      // Track password change
      trackEvent('password_changed', { role: authUser?.role });
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar contraseña');
    } finally { setPwdSaving(false); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-primary">Mi Perfil</h1>
        <p className="text-text-secondary mt-1">Administra tu información personal y credenciales de acceso.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Photo + Quick Info ── */}
        <div className="space-y-4">
          {/* Photo */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 flex flex-col items-center gap-4">
            <div className="relative">
              <div
                className="h-28 w-28 rounded-full border-4 border-white shadow-lg overflow-hidden bg-accent/10 flex items-center justify-center cursor-pointer group"
                onClick={() => fileInputRef.current?.click()}
              >
                {photoPreview ? (
                  <img src={photoPreview} alt="Foto" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-accent">
                    {name.charAt(0).toUpperCase() || '?'}
                  </span>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
                  <Camera className="h-6 w-6 text-white" />
                </div>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 h-8 w-8 bg-accent rounded-full flex items-center justify-center shadow-md hover:bg-accent/90 transition-colors"
                data-testid="photo-upload-button"
              >
                <Camera className="h-4 w-4 text-white" />
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="photo-file-input" />
            <div className="text-center">
              <p className="font-semibold text-slate-800 text-lg">{name || profile?.name}</p>
              <p className="text-sm text-slate-500">{profile?.email}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${ROLE_COLORS[profile?.role] || 'bg-slate-100 text-slate-600'}`}>
              {ROLE_LABELS[profile?.role] || profile?.role}
            </span>
            <p className="text-xs text-slate-400 text-center">Haz clic en la foto para cambiarla.<br />Máximo 500KB.</p>
          </div>

          {/* Read-only info */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Información de cuenta</h3>
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Email</p>
                <p className="text-sm font-medium text-slate-700">{profile?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-slate-400 shrink-0" />
              <div>
                <p className="text-xs text-slate-400">Rol</p>
                <p className="text-sm font-medium text-slate-700">{ROLE_LABELS[profile?.role]}</p>
              </div>
            </div>
            {profile?.buildings_detail?.length > 0 && (
              <div className="flex items-start gap-3">
                <Building className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-400">Conjuntos asignados</p>
                  {profile.buildings_detail.map(b => (
                    <p key={b.id} className="text-sm font-medium text-slate-700">{b.name} {b.city ? `· ${b.city}` : ''}</p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right: Editable Form + Password ── */}
        <div className="lg:col-span-2 space-y-6">
          {/* Edit form */}
          <form onSubmit={handleSaveProfile} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5" data-testid="profile-form">
            <h2 className="text-lg font-heading font-semibold text-slate-800">Información personal</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" /> Nombre completo
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 bg-slate-50 border-slate-200"
                  data-testid="name-input"
                  required
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" /> Teléfono
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+57 300 123 4567"
                  className="mt-1 bg-slate-50 border-slate-200"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" /> Email
                </Label>
                <Input
                  value={profile?.email || ''}
                  disabled
                  className="mt-1 bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed"
                  data-testid="email-readonly"
                />
                <p className="text-xs text-slate-400 mt-1">El email no es editable.</p>
              </div>
              <div>
                <Label className="text-slate-700 font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-slate-400" /> Rol
                </Label>
                <Input
                  value={ROLE_LABELS[profile?.role] || ''}
                  disabled
                  className="mt-1 bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed"
                  data-testid="role-readonly"
                />
                <p className="text-xs text-slate-400 mt-1">El rol debe ser cambiado por un administrador.</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={saving}
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 gap-2"
                data-testid="save-profile-button"
              >
                {saving ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </form>

          {/* Password change */}
          <form onSubmit={handleChangePassword} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5" data-testid="password-form">
            <h2 className="text-lg font-heading font-semibold text-slate-800">Cambiar contraseña</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="text-slate-700 font-medium">Contraseña actual</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPwd.current ? 'text' : 'password'}
                    value={currentPwd}
                    onChange={(e) => setCurrentPwd(e.target.value)}
                    className="bg-slate-50 border-slate-200 pr-10"
                    required
                    data-testid="current-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, current: !p.current }))} className="absolute right-3 top-2.5 text-slate-400">
                    {showPwd.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Nueva contraseña</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPwd.new ? 'text' : 'password'}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="bg-slate-50 border-slate-200 pr-10"
                    required
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, new: !p.new }))} className="absolute right-3 top-2.5 text-slate-400">
                    {showPwd.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {newPwd.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    <Req met={hasMin}   text="Mínimo 8 caracteres" />
                    <Req met={hasUpper} text="1 letra mayúscula" />
                    <Req met={hasNum}   text="1 número" />
                  </ul>
                )}
              </div>
              <div>
                <Label className="text-slate-700 font-medium">Confirmar contraseña</Label>
                <div className="relative mt-1">
                  <Input
                    type={showPwd.confirm ? 'text' : 'password'}
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    className={`bg-slate-50 border-slate-200 pr-10 ${confirmPwd && !pwdMatch ? 'border-destructive' : ''}`}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(p => ({ ...p, confirm: !p.confirm }))} className="absolute right-3 top-2.5 text-slate-400">
                    {showPwd.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPwd && !pwdMatch && (
                  <p className="text-xs text-destructive mt-1" data-testid="pwd-mismatch">Las contraseñas no coinciden</p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={pwdSaving || !hasMin || !hasUpper || !hasNum || !pwdMatch || !currentPwd}
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 gap-2"
                data-testid="save-password-button"
              >
                {pwdSaving ? 'Actualizando...' : 'Actualizar contraseña'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
